import * as THREE from 'three';
import AudioMixer from './AudioMixer.js';
import Experience from '../Experience.js';

export default class AudioManager {
    constructor() {
        this.experience = new Experience();
        this.scene = this.experience.scene;
        this.resources = this.experience.resources;
        this.camera = this.experience.camera;

        // AudioListenerをシーンに追加
        this.audioListener = new THREE.AudioListener();
        this.camera.instance.add(this.audioListener);
        // AudioMixerを初期化
        this.mixer = new AudioMixer(this.audioListener);

        // カスタムグループを追加
        this.mixer.createGroup('Music', 'BGM');
        this.mixer.createGroup('Bonfire', 'Master');

        // 音楽の参照を保存
        this.currentBGM = null;
        this.bonfireSound = null;
        this.ambientSounds = new Map();

        this.setupAudioGroups();
        this.setupAudioSettings();
        this.playBGM('mainBGM'); // デフォルトのBGMを再生
        this.playBonfireSound('bonfireSound'); // デフォルトの焚き火音を再生
    }

    setupAudioGroups() {
      // 1. まずMasterグループ（最上位）
      this.mixer.createGroup('Master');
      // 2. メインカテゴリをMasterの子として作成
      this.mixer.createGroup('BGM', 'Master');
      this.mixer.createGroup('Bonfire', 'Master');
      this.mixer.createGroup('SE', 'Master');

    }

    setupAudioSettings() {
        // 初期音量設定（Unity風）
        this.mixer.setGroupVolume('BGM', 0.7);
        this.mixer.setGroupVolume('Bonfire', 0.8);
        this.mixer.setGroupVolume('SE', 1.0);
        // this.mixer.setGroupVolume('Voice', 0.9);
        // this.mixer.setGroupVolume('Environment', 0.4);
        // this.mixer.setGroupVolume('UI', 1.0);

        // マスター音量
        this.mixer.setMasterVolume(0.8);
    }

    /**
     * BGMを再生（クロスフェード対応）
     */
    playBGM(bgmName, fadeInTime = 5.0, fadeOutTime = 1.5) {
        const audioBuffer = this.resources.items[bgmName];
        if (!audioBuffer) {
            console.error(`BGM "${bgmName}" not found`);
            return;
        }

        // 現在のBGMをフェードアウト
        if (this.currentBGM) {
            this.mixer.stopGroup('BGM', fadeOutTime);
        }

        // 新しいBGMをフェードイン
        this.currentBGM = this.mixer.playBGM(audioBuffer, 0.7, fadeInTime);

        // console.log(`Playing BGM: ${bgmName}`);
    }

    /**
     * 環境音を再生（ループ）
     */
    playAmbientSound(soundName, volume = 0.3, position = null) {
        const audioBuffer = this.resources.items[soundName];
        if (!audioBuffer) {
            console.error(`Ambient sound "${soundName}" not found`);
            return;
        }

        // 既存の環境音を停止
        if (this.ambientSounds.has(soundName)) {
            this.ambientSounds.get(soundName).audio.stop();
        }

        const options = {
            group: 'Environment',
            volume,
            loop: true,
            fadeInDuration: 1.0
        };

        if (position) {
            options.position = position;
        }

        const audioInfo = this.mixer.playAudio(audioBuffer, options);
        this.ambientSounds.set(soundName, audioInfo);
        
        return audioInfo;
    }

    /**
     * BGMを再生（クロスフェード対応）
     */
    playBonfireSound(soundName, fadeInTime = 2.0, fadeOutTime = 1.5) {
      const audioBuffer = this.resources.items[soundName];
      if (!audioBuffer) {
          console.error(`Bonfire Sound "${soundName}" not found`);
          return;
      }

      // 現在のBGMをフェードアウト
      if (this.bonfireSound) {
          this.mixer.stopGroup('Bonfire', fadeOutTime);
      }

      // 新しいBGMをフェードイン
      this.bonfireSound = this.mixer.playBonfireSound(audioBuffer, 0.5, fadeInTime);
      // console.log(`Playing BGM: ${bgmName}`);
    }

    playSE(soundName, volume = 1.0, pitch = 1.0) {
        const audioBuffer = this.resources.items[soundName];
        if (!audioBuffer) {
            console.error(`Character SE "${soundName}" not found`);
            return;
        }

        return this.mixer.playAudio(audioBuffer, {
            group: 'SE',
            volume,
            pitch,
            loop: false
        });
    }

    /**
     * キャラクター効果音
     */
    playCharacterSFX(soundName, volume = 1.0, pitch = 1.0) {
        const audioBuffer = this.resources.items[soundName];
        if (!audioBuffer) {
            console.error(`Character SFX "${soundName}" not found`);
            return;
        }

        return this.mixer.playAudio(audioBuffer, {
            group: 'Character',
            volume,
            pitch,
            loop: false
        });
    }

    /**
     * UI効果音
     */
    playUISFX(soundName, volume = 0.8) {
        const audioBuffer = this.resources.items[soundName];
        if (!audioBuffer) {
            console.error(`UI SFX "${soundName}" not found`);
            return;
        }

        return this.mixer.playAudio(audioBuffer, {
            group: 'UI',
            volume,
            loop: false
        });
    }

    /**
     * 3D空間の効果音
     */
    play3DSoundAtPosition(soundName, position, volume = 1.0, refDistance = 5) {
        const audioBuffer = this.resources.items[soundName];
        if (!audioBuffer) {
            console.error(`3D Sound "${soundName}" not found`);
            return;
        }

        return this.mixer.play3DSFX(audioBuffer, position, volume, refDistance);
    }

    /**
     * シーン遷移時の音楽管理
     */
    transitionToScene(sceneName) {
        switch (sceneName) {
            case 'menu':
                this.playBGM('menuBGM', 2.0);
                this.mixer.setGroupVolume('Environment', 0, 1.0);
                break;
                
            case 'camping':
                this.playBGM('campingBGM', 3.0);
                this.playAmbientSound('bonfireSound', 0.4);
                this.playAmbientSound('windSound', 0.2);
                this.mixer.setGroupVolume('Environment', 0.4, 2.0);
                break;
                
            case 'forest':
                this.playBGM('forestBGM', 2.5);
                this.playAmbientSound('birdSounds', 0.3);
                this.playAmbientSound('rustlingSounds', 0.2);
                this.mixer.setGroupVolume('Environment', 0.5, 2.0);
                break;
                
            case 'battle':
                this.playBGM('battleBGM', 1.0);
                this.mixer.setGroupVolume('SFX', 1.0, 0.5);
                this.mixer.setGroupVolume('Environment', 0.1, 1.0);
                break;
        }
    }

    /**
     * 設定画面用のメソッド
     */
    getAudioSettings() {
        return {
            masterVolume: this.mixer.masterVolume,
            bgmVolume: this.mixer.groups.get('BGM').volume,
            sfxVolume: this.mixer.groups.get('SFX').volume,
            voiceVolume: this.mixer.groups.get('Voice').volume,
            isMuted: this.mixer.isMuted
        };
    }

    applyAudioSettings(settings) {
        this.mixer.setMasterVolume(settings.masterVolume, 0.5);
        this.mixer.setGroupVolume('BGM', settings.bgmVolume, 0.5);
        this.mixer.setGroupVolume('SFX', settings.sfxVolume, 0.5);
        this.mixer.setGroupVolume('Voice', settings.voiceVolume, 0.5);
        this.mixer.setMasterMute(settings.isMuted);
    }

    /**
     * 一時停止（ゲームポーズ時）
     */
    pauseAll() {
        // BGMと環境音を一時停止
        this.mixer.setGroupVolume('BGM', 0.2, 0.3);
        this.mixer.setGroupVolume('Environment', 0.1, 0.3);
    }

    /**
     * 再開（ゲームポーズ解除時）
     */
    resumeAll() {
        // BGMと環境音を元の音量に戻す
        this.mixer.setGroupVolume('BGM', 0.7, 0.3);
        this.mixer.setGroupVolume('Environment', 0.4, 0.3);
    }

    /**
     * ランダム効果音再生（バリエーション対応）
     */
    playRandomSFX(baseName, count = 3, volume = 1.0) {
        const randomIndex = Math.floor(Math.random() * count) + 1;
        const soundName = `${baseName}${randomIndex}`;
        return this.playCharacterSFX(soundName, volume);
    }

    /**
     * 足音システム（地面の材質によって音を変更）
     */
    playFootstep(surfaceType, volume = 0.6) {
        const footstepSounds = {
            grass: 'footstepGrass',
            stone: 'footstepStone', 
            water: 'footstepWater',
            wood: 'footstepWood'
        };

        const soundName = footstepSounds[surfaceType] || 'footstepGrass';
        
        // ピッチをランダムに変更して自然さを演出
        const randomPitch = 0.9 + Math.random() * 0.2;
        
        return this.playCharacterSFX(soundName, volume, randomPitch);
    }

    /**
     * デバッグ情報
     */
    getDebugInfo() {
        const status = this.mixer.getStatus();
        status.currentBGM = this.currentBGM ? 'Playing' : 'None';
        status.ambientSounds = Array.from(this.ambientSounds.keys());
        return status;
    }

    /**
     * リソース解放
     */
    dispose() {
        this.mixer.dispose();
        this.ambientSounds.clear();
        this.currentBGM = null;
    }
}